import { RootState, ThunkResult } from "common/reducers";
import { handleErrors } from "common/reducers/Utils";
import { DocumentActions } from "common/reducers/document.actions";
import { RootDispatchType, RootGetState } from "common/store";
import { List } from "immutable";
import { IdsManagementActions } from "common/reducers/idsManagement.actions";
import { DocumentParamsRecord } from "common/records/index/DocumentParams";
import { list as listDocuments } from "common/api/document.service";
import { DocumentRecord, IDocumentData } from "common/records/Document";
import { EstimateActions } from "common/reducers/estimate.actions";
import { create, loadSignedDocument, notify, sign, validate, voidDocument } from "common/api/signedDocument.service";
import { RsfFormActions } from "common/reducers/rsfForm.actions";
import { formName as signaturesFormName } from "customer/Signatures/SignaturesSign";
import { DRAFT_SIGNED_DOCUMENT_ID, SignedDocumentActions } from "common/reducers/signedDocument.actions";
import { ISignedDocumentData } from "common/records/SignedDocument";
import { form } from "common/selectors/rsfForm.selectors";
import { addFlashAlert, DANGER, SUCCESS } from "common/components/FlashAlert";
import { NavigateFunction } from "react-router";
import { DocumentAsyncActions } from "common/reducers/document.async.actions";
import {
  currentSignedDocument,
  currentSignedDocumentId,
  signedDocument as signedDocumentSelector,
} from "common/selectors/signedDocument.selectors";
import { document as documentSelector } from "common/selectors/document.selectors";
import { Nullable } from "common/types";
import { IGlobalRequestOptions } from "common/api/Fetcher";
import { TaskAsyncActions } from "common/reducers/task.async.actions";
import { TaskActions } from "common/reducers/task.actions";
import { ITaskData } from "common/records/Task";
import { watchRedux } from "src/router/loaders/helper";
import { task as taskSelector } from "common/selectors/task.selectors";

export const SignedDocumentAsyncActions = {
  /**
   * Resets the singleton draft slot for a signature flow and points `currentId` at it.
   *
   * The signed-document store holds a single in-progress draft at `byId[DRAFT_SIGNED_DOCUMENT_ID]`
   * with one `currentId`. Every flow (contract, DaBella custom form, ad-hoc signature request) starts
   * by wholesale-replacing that slot via `receiveSignedDocument` (a `receive()`, not a merge) and
   * setting `currentId` to it — the reset-before-validate invariant every flow relies on. Centralizing
   * the pair here keeps the invariant in one place instead of duplicated across each entry point.
   *
   * @param seed - per-flow draft fields (e.g. `document_id`, `estimate_id`, `metadata`); the fixed
   *   draft `id` is applied here and must not be passed by callers.
   */
  initDraft: (seed: Omit<Partial<ISignedDocumentData>, "id"> = {}): ThunkResult<void> => {
    return (dispatch: RootDispatchType) => {
      dispatch(SignedDocumentActions.receiveSignedDocument({ ...seed, id: DRAFT_SIGNED_DOCUMENT_ID }));
      dispatch(SignedDocumentActions.setCurrentSignedDocumentId(DRAFT_SIGNED_DOCUMENT_ID));
    };
  },
  sign: (signatureId: string, navigate: NavigateFunction): ThunkResult<Promise<any>> => {
    return async (dispatch: RootDispatchType) => {
      const formRootPath = ["rsfForms", "byForm", signaturesFormName];
      try {
        const data = await sign(signatureId);
        window.location.assign(data.signed_document_recipient.rs_url);
      } catch (e: any) {
        navigate("/signatures/sign", { replace: true });

        if (e.status === 400 && e.data?.errors[0] === "Invalid Key") {
          dispatch(
            RsfFormActions.update({
              rootPath: formRootPath,
              name: "id",
              value: signatureId,
            }),
          );
          setTimeout(
            () =>
              dispatch(
                RsfFormActions.updateError({
                  rootPath: formRootPath,
                  name: "id",
                  error: "Invalid Signature ID",
                }),
              ),
            10,
          );
        }

        console.error(e);
      }
    };
  },
  load: (documentId: number): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      try {
        dispatch(SignedDocumentActions.fetchSignedDocument(documentId));
        const { signed_document } = await loadSignedDocument(documentId);
        dispatch(SignedDocumentActions.receiveSignedDocument(signed_document));
        if (signed_document.document) {
          dispatch(DocumentActions.receiveDocument(signed_document.document));
        }
      } catch (response) {
        handleErrors(response);
        dispatch(SignedDocumentActions.setSignedDocumentLoaded(documentId));
      }
    };
  },

  notify: (documentId: number, recipient?: number): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      try {
        await notify(documentId, recipient);
        addFlashAlert(SUCCESS, "Signature Document successfully sent.");
      } catch (response) {
        handleErrors(response);
      }
    };
  },

  listEstimateDocuments: (queryParams: DocumentParamsRecord): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      dispatch(IdsManagementActions.fetchModel("estimateDocuments", queryParams));
      dispatch(EstimateActions.setEstimateDocsLoaded(false));
      try {
        const response = await listDocuments(queryParams);
        dispatch(DocumentActions.receiveDocuments(response.documents));

        const ids = List(response.documents.map((document: IDocumentData) => document.id));
        dispatch(EstimateActions.receiveEstimateDocuments(response.documents));
        dispatch(IdsManagementActions.receiveModel("estimateDocuments", ids, response?.meta?.pagination));
        dispatch(EstimateActions.setEstimateDocsLoaded(true));
      } catch (response) {
        handleErrors(response);
        dispatch(IdsManagementActions.receiveModel("estimateDocuments", List()));
      }
    };
  },
  combineDocument: (
    document_ids: List<number>,
    name: string,
    jobId: Nullable<number>,
    options?: IGlobalRequestOptions,
  ): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      if (!jobId) {
        console.error("job id is undefined or null");
        addFlashAlert(DANGER, "Something went wrong");
        return;
      }
      await dispatch(
        options
          ? DocumentAsyncActions.combine(document_ids, name, jobId, options)
          : DocumentAsyncActions.combine(document_ids, name, jobId),
      );
    };
  },
  validateSignedDocument: (
    document: DocumentRecord,
    options: {
      estimateId?: number;
      metadata?: Record<string, unknown>;
      documentName?: string;
      isModified?: boolean;
    } = {},
  ): ThunkResult<Promise<boolean>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const { estimateId, metadata, documentName, isModified = false } = options;

      try {
        dispatch(SignedDocumentActions.setCurrentSignedDocumentId(DRAFT_SIGNED_DOCUMENT_ID));

        const response = await validate(document, isModified);

        // The validate response is a Partial<IDocumentData> and `receiveDocument` replaces the
        // stored record wholesale, so merge the partial onto the existing record to keep fields the
        // response omits — notably `url`/`file`, which the existing-saved-contract path needs so the
        // PDF behind the signature modal doesn't blank. Optional documentName then replaces the
        // server's UUID-based filename so callers can name the signed document (e.g. the contract flow).
        const existing = documentSelector(getState(), { documentId: document.id });
        const validatedDocument = {
          ...(existing ? (existing.toJS() as Partial<IDocumentData>) : {}),
          ...response.document,
          ...(documentName ? { name: documentName } : {}),
        };
        dispatch(DocumentActions.receiveDocument(validatedDocument));
        dispatch(SignedDocumentActions.createSignedDocument(response, estimateId, metadata));
        addFlashAlert(SUCCESS, "Document validated successfully");
        return true;
      } catch (response) {
        handleErrors(response, true);
        return false;
      }
    };
  },
  voidSignedDocument: (signedDocumentId: number, navigate: () => void): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const reason = form(getState(), { name: "voidSignedDocument" })?.get("reason");
      try {
        await voidDocument(signedDocumentId, reason);
        const existingRecord = signedDocumentSelector(getState(), { signedDocumentId });
        if (existingRecord) {
          const voidedSignedDocument = {
            ...(existingRecord.toJS() as any),
            status: "voided",
          };
          dispatch(SignedDocumentActions.receiveSignedDocument(voidedSignedDocument));
          const existingDocument = documentSelector(getState(), { documentId: existingRecord.document_id });
          if (existingDocument) {
            dispatch(
              DocumentActions.receiveDocument({
                ...(existingDocument.toJS() as any),
                signed_document: voidedSignedDocument,
              }),
            );
          }
        }
        addFlashAlert(SUCCESS, "Document voided successfully.");
        navigate();
      } catch (response) {
        handleErrors(response);
      }
    };
  },
  create: (
    document: DocumentRecord,
    options: {
      notify: boolean;
      message: string;
    },
  ): ThunkResult<Promise<number>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const signedDocument = currentSignedDocument(getState());
      if (!signedDocument) {
        throw new Error("signedDocument is undefined or null");
      }
      try {
        const { signed_document: sd } = await create(document, signedDocument, options);

        dispatch(SignedDocumentActions.setCurrentSignedDocumentId(sd.id));
        dispatch(SignedDocumentActions.receiveSignedDocument(sd));
        dispatch(DocumentActions.receiveDocument(sd.document));

        if (sd.document.id) {
          dispatch(IdsManagementActions.pushId(sd.document.id, "signableDocuments"));
        }
        const task = sd.document?.task;
        if (task) {
          const context = {
            model: "signedDocument",
            action: "",
          };
          task.context = context;
          dispatch(TaskActions.receiveTask(task));

          await watchRedux(
            (state) => taskSelector(state, { taskId: task.id })?.status === "finished",
            undefined,
            90_000,
          );
        }
        return sd.id;
      } catch (response) {
        handleErrors(response, true);
        return -1;
      }
    };
  },
};
